import * as React from 'react';
import Box from '@mui/material/Box';
import AddIcon from '@mui/icons-material/Add';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import {
    Avatar,
    CircularProgress,
    Divider,
    IconButton,
    Typography,
    Chip,
    Tooltip
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ClearIcon from '@mui/icons-material/Clear';
import api from '../../services/api';
import { useEvent } from '../../hooks/useEvent';
import HelpIcon from '@mui/icons-material/Help';

export default function FollowersContactsAutoComplete({ placeholder, cardId }) {
    const theme = useTheme();
    const emitter = useEvent();
    const [searchParam, setSearchParam] = React.useState('');
    const [contacts, setContacts] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [selectedContacts, setSelectedContacts] = React.useState([]);

    const debounceTimeout = React.useRef(null);
    const abortController = React.useRef(null);

    // Carregar os contatos iniciais para o card
    React.useEffect(() => {
        const fetchCardContacts = async () => {
            if (!cardId) return;
            setLoading(true);
            try {
                const { data } = await api.get(`/kanban/card/followers/${cardId}`);
                setContacts(data);
                setSelectedContacts(data);
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        };
        fetchCardContacts();
    }, [cardId]);

    // Limpar ao desmontar
    React.useEffect(() => {
        return () => {
            if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
            if (abortController.current) abortController.current.abort();
        };
    }, []);

    const handleSearchContact = async (value) => {
        if (!value || value.length < 2) {
            setContacts(selectedContacts);
            return;
        }

        // Cancelar requisição anterior
        if (abortController.current) {
            abortController.current.abort();
        }
        abortController.current = new AbortController();

        setLoading(true);
        try {
            const { data } = await api.get('/listContacts', {
                params: { searchParam: value.toLowerCase() },
                signal: abortController.current.signal
            });

            const newContacts = data?.contacts || [];
            const combinedContacts = [...newContacts];

            for (const selected of selectedContacts) {
                if (!newContacts.some(contact => contact.id === selected.id)) {
                    combinedContacts.push(selected);
                }
            }

            setContacts(combinedContacts);
        } catch (error) {
            if (error?.name !== 'CanceledError' && error?.code !== 'ERR_CANCELED') {
                console.error(error);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (event, newValue) => {
        setSearchParam(newValue);

        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }

        debounceTimeout.current = setTimeout(() => {
            handleSearchContact(newValue);
        }, 400);
    };

    const handleSelectContacts = async (newSelectedContacts) => {
        setSelectedContacts(newSelectedContacts);

        if (cardId) {
            try {
                setSaving(true);
                const followersIds = newSelectedContacts.map(contact => contact.id);
                await api.put(`/kanban/followers/${cardId}`, {
                    followersIds
                });
            } catch (error) {
                console.error('Error updating card contacts:', error);
            } finally {
                setSaving(false);
            }
        }
    };

    const handleClearSelection = async () => {
        if (cardId) {
            try {
                setSaving(true);
                await api.put(`/kanban/followers/${cardId}`, {
                    followersIds: []
                });
                setSelectedContacts([]);
                setSearchParam('');
                setContacts([]);
            } catch (error) {
                console.error('Error clearing card contacts:', error);
            } finally {
                setSaving(false);
            }
        }
    };

    const getSecondaryText = (option) => {
        if (option.isGroup) return 'Grupo';
        const parts = [];
        if (option.number) parts.push(`+${option.number}`);
        if (option.email) parts.push(option.email);
        if (option.contract) parts.push(`#${option.contract}`);
        return parts.join(' | ') || '';
    };

    const noOptionsLabel = React.useMemo(() => {
        if (loading) return 'Buscando...';
        if (searchParam.length > 0 && searchParam.length < 2) return 'Digite ao menos 2 caracteres...';
        if (searchParam.length >= 2 && contacts.length === 0) return 'Nenhum contato encontrado';
        return 'Digite para pesquisar por nome, telefone, email ou contrato...';
    }, [loading, searchParam, contacts.length]);

    return (
        <Autocomplete
            size="small"
            id="followers-contact-autocomplete"
            sx={{ width: '100%' }}
            options={contacts}
            autoHighlight
            multiple
            value={selectedContacts}
            inputValue={searchParam}
            onInputChange={handleInputChange}
            onChange={(event, newValue) => handleSelectContacts(newValue)}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            getOptionLabel={(option) => option ? `${option.name}` : ''}
            filterOptions={(x) => x}
            noOptionsText={noOptionsLabel}
            loading={loading}
            renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => (
                    <Chip
                        size="small"
                        avatar={<Avatar src={option.profilePicUrl} />}
                        label={option.name}
                        {...getTagProps({ index })}
                        sx={{
                            borderRadius: '5px',
                            '& .MuiChip-deleteIcon': {
                                color: theme.palette.mode === 'dark' ? '#FFFFFF80' : '#00000080'
                            }
                        }}
                    />
                ))
            }
            renderOption={(props, option) => (
                <Box
                    component="li"
                    key={option.id}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75 }}
                    {...props}
                >
                    <Avatar
                        src={option.profilePicUrl}
                        sx={{ width: 32, height: 32, flexShrink: 0 }}
                    />
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={500} noWrap>
                            {option.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                            {getSecondaryText(option)}
                        </Typography>
                    </Box>
                </Box>
            )}
            renderInput={(params) => (
                <TextField
                    {...params}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            padding: '4px 5px 4px 6px !important',
                            flexWrap: 'wrap'
                        }
                    }}
                    InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                            <>
                                <Typography sx={{
                                    marginLeft: '10px',
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    color: theme.palette.text.secondary
                                }}>
                                    {placeholder}
                                </Typography>
                                <Tooltip title="Os seguidores receberão as mesmas notificações que o solicitante">
                                    <HelpIcon sx={{
                                        width: '20px',
                                        marginLeft: '5px',
                                        color: (theme) => theme.palette.mode === 'dark' ? '#8a9596' : '#637585'
                                    }} />
                                </Tooltip>
                                <Divider sx={{ margin: '0px 10px' }} orientation="vertical" flexItem />
                                {params.InputProps.startAdornment}
                            </>
                        ),
                        endAdornment: (
                            <>
                                {(loading || saving) ? (
                                    <CircularProgress size={18} />
                                ) : (
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        {selectedContacts.length > 0 && (
                                            <IconButton onClick={handleClearSelection} sx={{ padding: 0 }}>
                                                <ClearIcon sx={{ color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000' }} />
                                            </IconButton>
                                        )}
                                        <AddIcon />
                                    </Box>
                                )}
                            </>
                        ),
                    }}
                />
            )}
        />
    );
}
